// apps/client/src/pages/leads/edm/components/TopicSelect.tsx
//
// Plan-v3 core Slice E1 (edm_preferences): the topic a campaign is sent
// under. Recipients who opted out of the topic on the preference centre are
// excluded at freeze and skipped at send. When the company has at least one
// topic the server requires one before scheduling (topic_required). With no
// topics this renders nothing.

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Skeleton } from '../../../../components/common/SkeletonLoader';
import { EDM_UI, edmErrorMessage } from '../../../../utils/edm';
import type { EdmTopic } from '../../../../hooks/useEdmContent';

const TopicSelect: React.FC<{
  value: string | null;
  onChange: (topicId: string | null) => void;
  getTopics: () => Promise<{ topics: EdmTopic[] }>;
  disabled?: boolean;
}> = ({ value, onChange, getTopics, disabled }) => {
  const [topics, setTopics] = useState<EdmTopic[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getTopics()
      .then((res) => {
        if (!cancelled) setTopics(res.topics);
      })
      .catch((err) => {
        if (!cancelled) setError(edmErrorMessage(err));
      });
    return () => {
      cancelled = true;
    };
  }, [getTopics]);

  if (error) return <div className={EDM_UI.errorBanner}>{error}</div>;
  if (topics === null) return <Skeleton className="h-10 w-full" />;
  if (topics.length === 0 && !value) return null;

  const known = value === null || topics.some((t) => t.id === value);
  return (
    <div data-testid="edm-topic-select">
      <label htmlFor="edm-topic" className={EDM_UI.label}>
        Topic
      </label>
      <select
        id="edm-topic"
        className={EDM_UI.input}
        value={value ?? ''}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value || null)}
      >
        <option value="">Choose a topic</option>
        {topics.map((t) => (
          <option key={t.id} value={t.id}>
            {t.name}
          </option>
        ))}
        {!known && value && <option value={value}>Removed topic</option>}
      </select>
      <p className="mt-1 text-xs text-gray-400">
        Required before you schedule. People who opted out of this topic do not receive the campaign.{' '}
        <Link to="/leads/edm/topics" className="text-blue-300 underline hover:text-blue-200">
          Manage topics
        </Link>
      </p>
    </div>
  );
};

export default TopicSelect;
